import * as THREE from 'three'

/**
 * Mouse trail mask buffer using ping-pong FBOs.
 * Only updates when the mouse is actually moving.
 */

const trailUpdateShader = {
    vertexShader: /* glsl */`
        varying vec2 vUv;
        void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
    `,
    fragmentShader: /* glsl */`
        uniform sampler2D tPrev;
        uniform vec2 uMouse;
        uniform float uMouseSpeed;
        uniform float uRadius;
        uniform float uStrength;
        uniform float uDissipation;
        uniform float uDiffusion;
        uniform float uSoftness;
        uniform vec2 uTexelSize;
        uniform float uAspect;

        varying vec2 vUv;

        void main() {
            float prev = texture2D(tPrev, vUv).r;
            float pL = texture2D(tPrev, vUv - vec2(uTexelSize.x, 0.0)).r;
            float pR = texture2D(tPrev, vUv + vec2(uTexelSize.x, 0.0)).r;
            float pT = texture2D(tPrev, vUv - vec2(0.0, uTexelSize.y)).r;
            float pB = texture2D(tPrev, vUv + vec2(0.0, uTexelSize.y)).r;

            float diffused = mix(prev, (pL + pR + pT + pB) * 0.25, uDiffusion);
            diffused *= uDissipation;

            // Paint mask with soft edges - correct for aspect ratio
            vec2 delta = vUv - uMouse;
            delta.x *= uAspect;
            float dist = length(delta);
            // uSoftness controls how gradual the fade is (0=sharp, 1=full gradient)
            float hardRadius = uRadius * (1.0 - uSoftness);
            float influence = 1.0 - smoothstep(hardRadius, uRadius, dist);
            float paint = influence * uStrength * min(uMouseSpeed * 15.0, 1.0);
            diffused = max(diffused, paint);

            gl_FragColor = vec4(clamp(diffused, 0.0, 1.0), 0.0, 0.0, 1.0);
        }
    `,
}

export function createFluidDisplacement(renderer, width, height) {
    const rtOptions = {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        format: THREE.RGBAFormat,
        type: THREE.HalfFloatType,
    }

    let rtA = new THREE.WebGLRenderTarget(width, height, rtOptions)
    let rtB = new THREE.WebGLRenderTarget(width, height, rtOptions)

    const material = new THREE.ShaderMaterial({
        vertexShader: trailUpdateShader.vertexShader,
        fragmentShader: trailUpdateShader.fragmentShader,
        uniforms: {
            tPrev: { value: null },
            uMouse: { value: new THREE.Vector2(-1, -1) },
            uMouseSpeed: { value: 0.0 },
            uRadius: { value: 0.06 },
            uStrength: { value: 1.0 },
            uDissipation: { value: 0.985 },
            uDiffusion: { value: 0.12 },
            uSoftness: { value: 0.7 },
            uAspect: { value: 1.0 },
            uTexelSize: { value: new THREE.Vector2(1 / width, 1 / height) },
        },
    })

    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material)
    const orthoScene = new THREE.Scene()
    orthoScene.add(quad)
    const orthoCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)

    // Raw mouse target (updated by events)
    const mouseTarget = new THREE.Vector2(-1, -1)
    // Smoothed position (lerped each frame toward target)
    const smoothMouse = new THREE.Vector2(-1, -1)
    const prevSmooth = new THREE.Vector2(-1, -1)
    let mouseSpeed = 0
    let hasContent = false
    let initialized = false

    const onMouseMove = (e) => {
        mouseTarget.set(e.clientX / window.innerWidth, 1.0 - e.clientY / window.innerHeight)
        if (!initialized) {
            smoothMouse.copy(mouseTarget)
            prevSmooth.copy(mouseTarget)
            initialized = true
        }
    }

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseleave', () => { mouseSpeed = 0 })

    let idleFrames = 0

    function update() {
        if (!initialized) return

        // Smooth lerp toward target
        prevSmooth.copy(smoothMouse)
        smoothMouse.lerp(mouseTarget, 0.35)

        const dx = smoothMouse.x - prevSmooth.x
        const dy = smoothMouse.y - prevSmooth.y
        mouseSpeed = Math.sqrt(dx * dx + dy * dy)

        // Skip if idle
        if (mouseSpeed < 0.00005 && !hasContent) return
        if (mouseSpeed < 0.00005) idleFrames++
        else idleFrames = 0
        if (idleFrames > 120) { hasContent = false; return }

        hasContent = true

        // Interpolate between prevSmooth and smoothMouse in sub-steps
        // to fill gaps when the mouse moves fast
        const dist = Math.sqrt(dx * dx + dy * dy)
        const radius = material.uniforms.uRadius.value
        const steps = Math.max(1, Math.ceil(dist / (radius * 0.5)))
        const stepSpeed = mouseSpeed / steps

        for (let i = 0; i < steps; i++) {
            const t = (i + 1) / steps
            const px = prevSmooth.x + dx * t
            const py = prevSmooth.y + dy * t

            material.uniforms.tPrev.value = rtA.texture
            material.uniforms.uMouse.value.set(px, py)
            material.uniforms.uMouseSpeed.value = stepSpeed

            renderer.setRenderTarget(rtB)
            renderer.render(orthoScene, orthoCamera)
            renderer.setRenderTarget(null)

            const tmp = rtA
            rtA = rtB
            rtB = tmp
        }
    }

    function getTexture() { return rtA.texture }

    function resize(w, h) {
        rtA.setSize(w, h)
        rtB.setSize(w, h)
        material.uniforms.uTexelSize.value.set(1 / w, 1 / h)
    }

    return { update, getTexture, resize, uniforms: material.uniforms }
}
